import type { EscPosEncoder } from "./escPosEncoder"
import type { RasterReceiptOptions } from "./receiptRasterRenderer"
import type { PayOrderPrintContent } from "@/api/business/types/printing"
import type { HandoverBizData } from "@/api/user/types/handover"
import type { CheckoutReceiptFooterContext } from "@/utils/checkoutReceiptFooter"
import { getReceiptItemRemark } from "@repo/teashop-utils"
import dayjs from "dayjs"
import { buildHandoverDiscountPrintView } from "@/features/printing/handoverDiscountStats"
import { shiftOpeningSummary } from "@/layout/components/NavigationBar/shiftOpeningSummary"
import { useUserStore } from "@/store/modules/user"
import { buildCheckoutReceiptFooterLines } from "@/utils/checkoutReceiptFooter"
import { getHandoverPrintEmployees } from "@/utils/handoverPrintEmployees"
import { convertCentsToYuan } from "@/utils/math"
import { buildTableChargeReceiptView } from "@/utils/tableChargePrintDetails"

const RECEIPT_BOTTOM_FEED_LINES = 4
const PLAIN_TEXT_TEMPLATE_TOKEN_PATTERN = /<(CB|L|N|CUT)>/g

export interface ReceiptTextSize {
  width: number
  height: number
}

export interface ReceiptStyleOptions {
  renderMode?: "native" | "raster"
  printWidth?: string
  raster?: RasterReceiptOptions
  titleSize?: ReceiptTextSize
  bodySize?: ReceiptTextSize
  lineSpacing?: number
  lineFeedGap?: number
  titleBold?: boolean
  bodyBold?: boolean
}

export interface TableChargePrintDetail {
  label?: string
  tableName?: string
  sourceText?: string
  ruleText?: string
  startTime?: string
  endTime?: string
  durationText?: string
  pauseDurationText?: string
  pauseSummaryText?: string
  billingEndTime?: string
  amount?: string
  originalAmount?: string
}

export interface RechargeReceiptData {
  memberName?: string
  memberPhone?: string
  balanceBefore: number
  rechargeAmount: number
  giftAmount?: number
  balanceAfter: number
}

type PayOrderPrintContentWithTableCharge = PayOrderPrintContent & {
  tableChargeDetails?: TableChargePrintDetail[]
}

interface PackageChildReceiptItem {
  itemName?: string | null
  itemQuantity?: string | number | null
  count?: number | null
}

interface ReceiptItemWithPackageChildren {
  brandPointRedeemed?: boolean | null
  remark?: string | null
  packageChildItems?: PackageChildReceiptItem[] | null
}

/**
 * Generate Handover Receipt ESC/POS Commands
 */
export function generateHandoverReceipt(data: HandoverBizData, style?: ReceiptStyleOptions): (encoder: EscPosEncoder) => void {
  return (encoder: EscPosEncoder) => {
    const userStore = useUserStore()
    const shopName = userStore.shopName || "Tea Shop"
    const receiptLayout = getReceiptLayout(style?.printWidth ?? userStore.printSetting?.printWidth, style?.bodySize?.width)

    encoder
      .initialize()
    applyReceiptLineSpacing(encoder, style)
    encoder
      .align("CENTER")
    applyReceiptTitleStyle(encoder, style)
    encoder
      .text(shopName)
      .newline()
    applyReceiptBodyStyle(encoder, style)
    encoder
      .text("\n--- 交班单 ---\n")
      .align("LEFT")
      .newline()

    // Basic Info
    encoder.text(`交班单号: ${data.merchantShiftHandoverCode || "--"}\n`)
    encoder.text(`统计开始: ${data.handoverStartTime || "--"}\n`)
    encoder.text(`交班时间: ${data.handoverEndTime || "--"}\n`)
    const employees = getHandoverPrintEmployees(data)
    encoder.text(`开班员工: ${employees.opener}\n`)
    encoder.text(`交班员工: ${employees.operator}\n`)
    for (const item of shiftOpeningSummary(data.opening)) {
      const safeValue = Array.from(item.value, char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 ? " " : char).join("")
      encoder.text(`${item.label}: ${safeValue}\n`)
    }
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    // Summary
    encoder.align("CENTER").text("交班汇总\n").align("LEFT")
    printRow(encoder, "本班总额", convertCentsToYuan(data.totalAmount ?? 0), receiptLayout.totalChars)
    printRow(encoder, "本班实收", convertCentsToYuan(data.totalIncome ?? 0), receiptLayout.totalChars)
    printRow(encoder, "现金收支", convertCentsToYuan(data.cashIncome ?? 0), receiptLayout.totalChars)
    printRow(encoder, "备用金", convertCentsToYuan(data.retainedCash ?? 0), receiptLayout.totalChars)
    printRow(encoder, "上交现金", convertCentsToYuan(data.handInCash ?? 0), receiptLayout.totalChars)
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    const discountPrintView = buildHandoverDiscountPrintView(data.discountStatList)
    if (discountPrintView.rows.length > 0) {
      encoder.align("CENTER").text("优惠统计\n").align("LEFT")
      printRow3(encoder, "优惠类型", "笔数", "金额", receiptLayout)
      discountPrintView.rows.forEach((item) => {
        printRow3(encoder, item.name, item.count, item.amount, receiptLayout)
      })
      printRow(encoder, "优惠净额合计", discountPrintView.totalAmount, receiptLayout.totalChars)
      encoder.text("-".repeat(receiptLayout.totalChars) + "\n")
    }

    // Payment Composition
    encoder.align("CENTER").text("收款构成\n").align("LEFT")
    printRow3(encoder, "类型", "笔数", "金额", receiptLayout)
    const paymentDetails = data.detailList?.filter((item: any) => (item.totalPayAmount ?? 0) > 0) || []
    paymentDetails.forEach((item: any) => {
      printRow3(
        encoder,
        item.paymentType || "",
        String(item.totalNumber),
        convertCentsToYuan(item.totalPayAmount ?? 0),
        receiptLayout,
      )
    })
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    // Transaction Statistics
    encoder.align("CENTER").text("交易统计\n").align("LEFT")
    printRow3(encoder, "类型", "笔数", "金额", receiptLayout)

    const defaultSort = [1, 2, 6, 5, 3, 4]
    const transactionData = data.detailBizList?.filter((item: any) => item.totalBizType != 4)?.sort((a: any, b: any) => {
      const indexA = defaultSort.indexOf(a.totalBizType ?? 0)
      const indexB = defaultSort.indexOf(b.totalBizType ?? 0)
      return indexA - indexB
    }) ?? []

    transactionData.forEach((item: any) => {
      printRow3(
        encoder,
        item.name?.trim() || getHandoverType(item.totalBizType!),
        String(item.totalNumber),
        convertCentsToYuan(item.totalPayAmount ?? 0),
        receiptLayout,
      )
    })

    encoder.newline()
    encoder.align("CENTER").text(`打印时间: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}\n`)

    feedAndCut(encoder)
  }
}

/**
 * Generate member recharge receipt ESC/POS commands.
 */
export function generateRechargeReceipt(data: RechargeReceiptData, style?: ReceiptStyleOptions): (encoder: EscPosEncoder) => void {
  return (encoder: EscPosEncoder) => {
    const userStore = useUserStore()
    const shopName = userStore.shopName || "Tea Shop"
    const receiptLayout = getReceiptLayout(style?.printWidth ?? userStore.printSetting?.printWidth, style?.bodySize?.width)

    encoder.initialize()
    applyReceiptLineSpacing(encoder, style)
    encoder.align("CENTER")
    applyReceiptTitleStyle(encoder, style)
    encoder.text(shopName).newline()
    applyReceiptBodyStyle(encoder, style)
    encoder.text("\n--- 会员充值单 ---\n").align("LEFT").newline()

    encoder.text(`会员名称: ${data.memberName || "--"}\n`)
    encoder.text(`手机号: ${data.memberPhone || "--"}\n`)
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")
    printRow(encoder, "现有余额", convertCentsToYuan(data.balanceBefore, false, 2), receiptLayout.totalChars)
    printRow(encoder, "充值金额", convertCentsToYuan(data.rechargeAmount, false, 2), receiptLayout.totalChars)
    if ((data.giftAmount ?? 0) > 0) {
      printRow(encoder, "赠送金额", convertCentsToYuan(data.giftAmount ?? 0, false, 2), receiptLayout.totalChars)
    }
    printRow(encoder, "充值后余额", convertCentsToYuan(data.balanceAfter, false, 2), receiptLayout.totalChars)

    encoder.newline()
    encoder.align("CENTER").text(`打印时间: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}\n`)
    feedAndCut(encoder)
  }
}

/**
 * Generate Order Receipt ESC/POS Commands
 */
export function generateOrderReceipt(
  data: PayOrderPrintContent,
  style?: ReceiptStyleOptions,
  footerContext?: CheckoutReceiptFooterContext,
): (encoder: EscPosEncoder) => void {
  return (encoder: EscPosEncoder) => {
    const orderData = data as PayOrderPrintContentWithTableCharge
    const userStore = useUserStore()
    const shopName = data.shopName || userStore.shopName || "Tea Shop"
    const receiptLayout = getReceiptLayout(style?.printWidth ?? userStore.printSetting?.printWidth, style?.bodySize?.width)

    encoder
      .initialize()
    applyReceiptLineSpacing(encoder, style)
    encoder
      .align("CENTER")
    applyReceiptTitleStyle(encoder, style)
    encoder
      .text(shopName)
      .newline()
    applyReceiptBodyStyle(encoder, style)
    encoder
      .text(`\n--- ${data.printType === 1 ? "预结单" : "结账单"} ---\n`)
      .align("LEFT")
      .newline()

    // Order Info Main
    data.orderInfoMain?.forEach(info => {
      encoder.text(`${info.text}: ${info.value}\n`)
    })
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    // Order Items
    encoder.align("CENTER").text("商品明细\n").align("LEFT")
    printRow4(encoder, "商品", "数量", "小计", "优惠价", receiptLayout)
    data.orderItems?.forEach(item => {
      const receiptItem = item as ReceiptItemWithPackageChildren
      if (receiptItem.brandPointRedeemed) {
        encoder.text("[积]\n")
      }
      const name = item.itemName ?? ""
      const qty = String(item.itemQuantity ?? "")
      const subtotal = item.itemTotalFee ?? "0.00"
      const payFee = item.itemPayFee ?? "0.00"
      const chunks = splitByByteLength(name, receiptLayout.row4.w1)

      if (chunks.length > 0) {
        printRow4(encoder, chunks[0], qty, subtotal, payFee, receiptLayout)
        for (let i = 1; i < chunks.length; i++) {
          encoder.text(chunks[i] + "\n")
        }
      }
      printPackageChildOrderRows(encoder, receiptItem, receiptLayout)
      const remark = getReceiptItemRemark(receiptItem)
      if (remark) {
        encoder.text(`备注：${remark}\n`)
      }
    })
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    printTableChargeDetails(encoder, orderData.tableChargeDetails, receiptLayout)

    // Fees
    data.totalFeeInfo?.forEach(fee => {
      printRow(encoder, fee.text ?? "", fee.value ?? "", receiptLayout.totalChars)
    })

    // Preferential Info (Discounts) - Added this block
    if (data.preferentialInfo && data.preferentialInfo.length > 0) {
      encoder.text("-".repeat(receiptLayout.totalChars) + "\n")
      data.preferentialInfo.forEach(pref => {
        printRow(encoder, `${pref.text || pref.name}`, pref.value ?? "", receiptLayout.totalChars)
      })
    }
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")

    // Footer / Totals
    if (data.payFee) {
      printRow(encoder, "应付金额", data.payFee, receiptLayout.totalChars)
    }

    // Payments
    if (data.payInfo && data.payInfo.length > 0) {
      data.payInfo.forEach(pay => {
        printRow(encoder, pay.text || pay.name || "", pay.value || "", receiptLayout.totalChars)
      })
      if (data.totalPay) {
        printRow(encoder, "支付合计", data.totalPay, receiptLayout.totalChars)
      }
    }

    if (data.pendingFee) {
      printRow(encoder, "会员挂账", data.pendingFee, receiptLayout.totalChars)
    }

    if (data.printType === 0) {
      const footerLines = buildCheckoutReceiptFooterLines(
        footerContext?.config ?? userStore.printSetting?.checkoutReceiptFooter,
        footerContext?.shopInfo ?? userStore.getShopInfo(),
      )
      if (footerLines.length > 0) {
        encoder.align("LEFT").text("-".repeat(receiptLayout.totalChars) + "\n")
        footerLines.forEach(line => encoder.text(`${line}\n`))
      }
    }

    encoder.newline()
    encoder.align("CENTER").text("谢谢惠顾\n")
    encoder.newline()
    encoder.align("CENTER").text(`打印时间: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}\n`)

    feedAndCut(encoder)
  }
}

export interface ItemOrderReceiptData {
  tableName?: string
  sourceName: string
  items: Array<{
    itemName?: string
    count?: number
    isSend?: number
    packageChildItems?: PackageChildReceiptItem[] | null
  }>
}

export function generateItemOrderReceipt(data: ItemOrderReceiptData, style?: ReceiptStyleOptions): (encoder: EscPosEncoder) => void {
  return (encoder: EscPosEncoder) => {
    const userStore = useUserStore()
    const receiptLayout = getReceiptLayout(style?.printWidth ?? userStore.printSetting?.printWidth, style?.bodySize?.width)
    encoder.initialize()
    applyReceiptLineSpacing(encoder, style)
    encoder.align("CENTER")
    applyReceiptTitleStyle(encoder, style)
    encoder.text(userStore.shopName || "Tea Shop").newline()
    applyReceiptBodyStyle(encoder, style)
    encoder.text(`\n--- ${data.sourceName} ---\n`).align("LEFT")
    encoder.text(`桌台: ${data.tableName || "--"}\n`)
    encoder.text("-".repeat(receiptLayout.totalChars) + "\n")
    data.items.forEach((item) => {
      encoder.text(`${item.isSend === 1 ? "[赠] " : ""}${item.itemName || "--"} x${item.count || 0}\n`)
      item.packageChildItems?.forEach((child) => {
        encoder.text(`  ${child.itemName || "--"} ${formatChildQuantity(child)}\n`)
      })
    })
    encoder.newline().align("CENTER").text(`打印时间: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}\n`)
    feedAndCut(encoder)
  }
}

function printPackageChildOrderRows(
  encoder: EscPosEncoder,
  item: ReceiptItemWithPackageChildren,
  receiptLayout: ReceiptLayout,
) {
  item.packageChildItems?.forEach((child) => {
    const name = `  ${child.itemName || "--"}`
    printRow(encoder, name, formatChildQuantity(child), receiptLayout.totalChars)
  })
}

function formatChildQuantity(child: PackageChildReceiptItem) {
  const quantity = child.itemQuantity ?? child.count ?? ""
  if (quantity === "") {
    return ""
  }
  const quantityText = String(quantity)
  return quantityText.startsWith("x") || quantityText.startsWith("×") ? quantityText : `x${quantityText}`
}

export function generatePlainTextReceipt(content: string, style?: ReceiptStyleOptions): (encoder: EscPosEncoder) => void {
  return (encoder: EscPosEncoder) => {
    encoder.initialize()
    applyReceiptLineSpacing(encoder, style)
    applyReceiptBodyStyle(encoder, style)
    printPlainTextTemplate(encoder, content, style)
    feedAndCut(encoder)
  }
}

// Helpers

function feedAndCut(encoder: EscPosEncoder) {
  for (let i = 0; i < RECEIPT_BOTTOM_FEED_LINES; i++) {
    encoder.newline()
  }
  encoder.cut()
}

function applyReceiptLineSpacing(encoder: EscPosEncoder, style?: ReceiptStyleOptions) {
  if (typeof style?.lineSpacing === "number") {
    encoder.lineSpacing(style.lineSpacing)
  }
  if (typeof style?.lineFeedGap === "number") {
    encoder.lineFeedGap(style.lineFeedGap)
  }
}

function applyReceiptSize(encoder: EscPosEncoder, size: ReceiptTextSize | undefined, defaultWidth: number, defaultHeight: number) {
  encoder.size(size?.width ?? defaultWidth, size?.height ?? defaultHeight)
}

function applyReceiptBold(encoder: EscPosEncoder, active: boolean | undefined) {
  if (typeof active === "boolean") {
    encoder.bold(active)
  }
}

function applyReceiptTitleStyle(encoder: EscPosEncoder, style?: ReceiptStyleOptions) {
  encoder.textRole("title")
  applyReceiptBold(encoder, style?.titleBold)
  applyReceiptSize(encoder, style?.titleSize, 1, 1)
}

function applyReceiptBodyStyle(encoder: EscPosEncoder, style?: ReceiptStyleOptions) {
  encoder.textRole("body")
  applyReceiptSize(encoder, style?.bodySize, 0, 0)
  if (typeof style?.bodyBold === "boolean") {
    encoder.bold(style.bodyBold)
  } else if (typeof style?.titleBold === "boolean") {
    encoder.bold(false)
  }
}

function printPlainTextTemplate(encoder: EscPosEncoder, content: string, style?: ReceiptStyleOptions) {
  let cursor = 0
  for (const match of content.matchAll(PLAIN_TEXT_TEMPLATE_TOKEN_PATTERN)) {
    const index = match.index ?? cursor
    if (index > cursor) {
      encoder.text(content.slice(cursor, index))
    }
    switch (match[1]) {
      case "CB":
        encoder.align("CENTER")
        applyReceiptTitleStyle(encoder, style)
        break
      case "L":
        encoder.align("LEFT")
        break
      case "N":
        applyReceiptBodyStyle(encoder, style)
        break
      case "CUT":
        break
    }
    cursor = index + match[0].length
  }
  if (cursor < content.length) {
    encoder.text(content.slice(cursor))
  }
}

function getHandoverType(type: number) {
  if (type == 1) return "现结"
  else if (type == 2) return "会员卡充值"
  else if (type == 3) return "挂单销账"
  else if (type == 4) return "记账本收支"
  else if (type == 5) return "挂单"
  else if (type == 6) return "会员卡消费"
  else return "未知"
}

type ReceiptLayout = {
  totalChars: number
  row3: {
    w1: number
    w2: number
    w3: number
  }
  row4: {
    w1: number
    w2: number
    w3: number
    w4: number
  }
}

function allocateReceiptColumns(baseWidths: number[], totalChars: number) {
  const baseTotal = baseWidths.reduce((sum, width) => sum + width, 0)
  const exactWidths = baseWidths.map(width => width * totalChars / baseTotal)
  const result = exactWidths.map(width => Math.max(1, Math.floor(width)))
  let remaining = totalChars - result.reduce((sum, width) => sum + width, 0)
  const distributionOrder = exactWidths
    .map((width, index) => ({ index, remainder: width - Math.floor(width) }))
    .sort((left, right) => right.remainder - left.remainder)
  for (let index = 0; remaining > 0; index += 1, remaining -= 1) {
    result[distributionOrder[index % distributionOrder.length].index] += 1
  }
  return result
}

function getReceiptLayout(printWidth?: string, bodyWidth = 0): ReceiptLayout {
  const normalized = String(printWidth || "").trim().toLowerCase()
  const baseTotalChars = normalized.includes("80") ? 48 : 32
  const widthMultiplier = Math.max(1, Math.min(3, Math.round(bodyWidth) + 1))
  const totalChars = Math.max(8, Math.floor(baseTotalChars / widthMultiplier))
  const baseRow3 = normalized.includes("80") ? [24, 9, 15] : [16, 6, 10]
  const baseRow4 = normalized.includes("80") ? [14, 8, 13, 13] : [9, 5, 9, 9]
  const [row3w1, row3w2, row3w3] = allocateReceiptColumns(baseRow3, totalChars)
  const [row4w1, row4w2, row4w3, row4w4] = allocateReceiptColumns(baseRow4, totalChars)
  return {
    totalChars,
    row3: { w1: row3w1, w2: row3w2, w3: row3w3 },
    row4: { w1: row4w1, w2: row4w2, w3: row4w3, w4: row4w4 },
  }
}

function printRow(encoder: EscPosEncoder, label: string, value: string, totalWidth: number) {
  const labelLen = getByteLen(label)
  const valueLen = getByteLen(value)
  const spaces = Math.max(0, totalWidth - labelLen - valueLen)
  encoder.text(label + " ".repeat(spaces) + value + "\n")
}

function printRow3(encoder: EscPosEncoder, col1: string, col2: string, col3: string, layout: ReceiptLayout) {
  const { w1, w2, w3 } = layout.row3
  encoder.text(padRight(col1, w1) + padRight(col2, w2) + padLeft(col3, w3) + "\n")
}

function printRow4(encoder: EscPosEncoder, col1: string, col2: string, col3: string, col4: string, layout: ReceiptLayout) {
  const { w1, w2, w3, w4 } = layout.row4
  encoder.text(
    padRight(col1, w1) +
    padRight(col2, w2) +
    padLeft(col3, w3) +
    padLeft(col4, w4) + "\n",
  )
}

function printTableChargeDetails(
  encoder: EscPosEncoder,
  details: TableChargePrintDetail[] | undefined,
  layout: ReceiptLayout,
) {
  const receiptView = buildTableChargeReceiptView(details)
  if (!receiptView.groups.length && !receiptView.startTime && !receiptView.endTime) {
    return
  }

  encoder.align("CENTER").text("桌台费用明细\n").align("LEFT")
  if (receiptView.startTime) {
    encoder.text(formatReceiptLine(`计费开始 ${receiptView.startTime}`, layout.totalChars) + "\n")
  }
  if (receiptView.endTime) {
    encoder.text(formatReceiptLine(`计费结束 ${receiptView.endTime}`, layout.totalChars) + "\n")
  }
  receiptView.groups.forEach((group) => {
    if (group.title) {
      encoder.text(formatReceiptLine(group.title, layout.totalChars) + "\n")
    }
    group.rows.forEach((row) => {
      printRow(encoder, row.label, row.amount, layout.totalChars)
    })
  })
  encoder.text("-".repeat(layout.totalChars) + "\n")
}

function formatReceiptLine(value: string, totalWidth: number) {
  const chunks = splitByByteLength(value, totalWidth)
  return chunks.join("\n")
}

function splitByByteLength(value: string, maxBytesPerLine: number) {
  if (!value) return [""]
  const lines: string[] = []
  let currentLine = ""

  for (const char of value) {
    const next = currentLine + char
    if (getByteLen(next) > maxBytesPerLine && currentLine) {
      lines.push(currentLine)
      currentLine = char
    } else {
      currentLine = next
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }
  return lines.length > 0 ? lines : [""]
}

function getByteLen(val: string) {
  let len = 0
  for (let i = 0; i < val.length; i++) {
    // 如果字符编码 > 255，则占用 2 字节（中日韩等双字节字符）
    if (val.charCodeAt(i) > 255) {
      len += 2
    } else {
      len += 1
    }
  }
  return len
}

function padRight(str: string, len: number) {
  const strLen = getByteLen(str)
  if (strLen >= len) return str // overflow?
  return str + " ".repeat(len - strLen)
}

function padLeft(str: string, len: number) {
  const strLen = getByteLen(str)
  if (strLen >= len) return str
  return " ".repeat(len - strLen) + str
}
